import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowUp, Loader2, BookOpen, GraduationCap, HelpCircle, AlertTriangle, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { streamAsk, withCitations } from "@/lib/assistant";

const MdLink = ({ href, children }) =>
  href?.startsWith("/") ? (
    <Link to={href} className="mx-0.5 inline-flex items-center rounded-md bg-lasso-sky/40 px-1.5 text-[11px] font-bold text-lasso-navy no-underline hover:bg-lasso-sky" data-testid="ask-citation-link">{children}</Link>
  ) : (
    <a href={href} target="_blank" rel="noreferrer" className="underline">{children}</a>
  );

function AssistantBubble({ m }) {
  if (m.not_covered) {
    return (
      <div className="flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="ask-not-covered">
        <HelpCircle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{m.content}</span>
      </div>
    );
  }
  if (m.error) {
    return (
      <div className="flex gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="ask-error">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{m.content}</span>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-lasso-line bg-white p-5" data-testid="ask-answer">
      <div className="lesson-prose text-sm">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: MdLink }}>{withCitations(m.content || "", m.sources)}</ReactMarkdown>
        {m.streaming && <span className="ml-1 inline-block h-4 w-1.5 animate-pulse bg-lasso-ink align-middle" />}
      </div>
      {m.sources?.length > 0 && (
        <div className="mt-4 border-t border-lasso-line pt-3" data-testid="ask-sources">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Sources</p>
          <div className="flex flex-wrap gap-2">
            {m.sources.map((s) => (
              <Link key={s.n} to={s.url} className="inline-flex items-center gap-1.5 rounded-full border border-lasso-line px-3 py-1 text-xs font-semibold transition-colors hover:border-lasso-ink" data-testid={`ask-source-${s.type}-${s.id}`}>
                {s.type === "sop" ? <BookOpen className="h-3 w-3 text-lasso-deep" /> : <GraduationCap className="h-3 w-3 text-lasso-deep" />}[{s.n}] {s.title}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ChatThread({ conversationId, onCreated, compact = false }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const skipLoad = useRef(null);
  const bottom = useRef(null);

  useEffect(() => {
    api.get("/assistant/suggestions").then((r) => setSuggestions(r.data));
  }, []);

  useEffect(() => {
    if (!conversationId) return setMessages([]);
    if (skipLoad.current === conversationId) return;
    api.get(`/assistant/conversations/${conversationId}`).then((r) => setMessages(r.data.messages)).catch(() => setMessages([]));
  }, [conversationId]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const updateLast = (fn) => setMessages((ms) => [...ms.slice(0, -1), fn(ms[ms.length - 1])]);

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || busy) return;
    setInput("");
    setBusy(true);
    setMessages((ms) => [...ms, { role: "user", content: question, id: `u-${Date.now()}` }, { role: "assistant", content: "", streaming: true, id: `a-${Date.now()}` }]);
    try {
      await streamAsk({ question, conversation_id: conversationId || null }, (ev) => {
        if (ev.type === "start" && !conversationId) {
          skipLoad.current = ev.conversation_id;
          onCreated?.(ev.conversation_id, ev.title);
        } else if (ev.type === "delta") {
          updateLast((m) => ({ ...m, content: m.content + ev.content }));
        } else if (ev.type === "done") {
          updateLast(() => ev.message);
        } else if (ev.type === "error") {
          updateLast(() => ev.message_obj || { role: "assistant", content: ev.message, error: true });
        }
      });
    } catch (e) {
      updateLast(() => ({ role: "assistant", content: e.message, error: true }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="ask-thread">
      <div className={`min-h-0 flex-1 space-y-5 overflow-y-auto ${compact ? "px-1" : "pr-2"}`}>
        {messages.length === 0 && (
          <div className="py-6" data-testid="ask-empty">
            <Sparkles className="h-6 w-6 text-lasso-deep" />
            <p className={`mt-4 font-display font-bold ${compact ? "text-base" : "text-xl"}`}>Ask anything about how we do things at Lasso.</p>
            <p className="mt-2 text-sm text-muted-foreground">Answers come only from published SOPs and course lessons, with sources linked.</p>
            <div className="mt-6 grid gap-2">
              {suggestions.map((s, i) => (
                <button key={s} onClick={() => send(s)} className="rounded-xl border border-lasso-line bg-white px-4 py-3 text-left text-sm transition-colors hover:border-lasso-ink" data-testid={`ask-suggestion-${i}`}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) =>
          m.role === "user" ? (
            <div key={m.id} className="flex justify-end"><p className="max-w-[85%] rounded-2xl rounded-br-md bg-lasso-ink px-4 py-3 text-sm text-white" data-testid="ask-user-message">{m.content}</p></div>
          ) : (
            <div key={m.id}>{m.streaming && !m.content ? <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="ask-thinking"><Loader2 className="h-4 w-4 animate-spin" /> Searching SOPs…</div> : <AssistantBubble m={m} />}</div>
          )
        )}
        <div ref={bottom} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex items-end gap-2 rounded-2xl border border-lasso-line bg-white p-2 focus-within:border-lasso-ink">
        <textarea
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask about an SOP… e.g. What do I do if a resident smells gas?"
          className="max-h-32 min-h-[40px] flex-1 resize-none bg-transparent px-3 py-2 text-sm outline-none"
          data-testid="ask-input"
        />
        <button type="submit" disabled={busy || !input.trim()} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-lasso-ink text-white transition-colors hover:bg-lasso-navy disabled:opacity-40" data-testid="ask-send-button" aria-label="Send">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
}
